import React, { useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { ChemInline } from '../../components/ChemInline';
import {
  formQuestionGroupKey,
  groupFormQuestionsByName,
  questionDocumentIds,
  selectedCountInGroup,
  toggleSelectedGroup,
  toggleSelectedId,
} from './formQuestionGroups';

function questionCheckboxLabel(row: DynamicEntityRow): string {
  const title = String(row.title ?? '').trim();
  const type = String(row.answer_type ?? '').trim();
  return title || String(row.documentId ?? '');
}

type Props = {
  label: string;
  questions: DynamicEntityRow[];
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  loading?: boolean;
  required?: boolean;
};

export function FormQuestionGroupedCheckboxField({
  label,
  questions,
  selectedIds,
  onChange,
  loading = false,
  required = false,
}: Props) {
  const groups = useMemo(() => groupFormQuestionsByName(questions), [questions]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || !groups.length) return;
    const keys = groups
      .filter((group) =>
        group.questions.some((q) => selectedSet.has(String(q.documentId ?? '').trim())),
      )
      .map((group) => formQuestionGroupKey(group.name));
    setExpanded(keys);
    setSeeded(true);
  }, [groups, seeded, selectedSet]);

  function setGroupExpanded(key: string, next: boolean) {
    setExpanded((current) => {
      const has = current.includes(key);
      if (next) return has ? current : [...current, key];
      return has ? current.filter((item) => item !== key) : current;
    });
  }

  return (
    <Box data-testid="form-question-grouped-checkboxes">
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
        <FormLabel required={required}>{label}</FormLabel>
        <Typography variant="caption" color="text.secondary">
          {selectedIds.length} dipilih
        </Typography>
      </Stack>
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Memuat pertanyaan…
        </Typography>
      ) : !groups.length ? (
        <Typography variant="body2" color="text.secondary">
          Belum ada form question.
        </Typography>
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {groups.map((group) => {
            const key = formQuestionGroupKey(group.name);
            const groupIds = questionDocumentIds(group.questions);
            const selectedInGroup = selectedCountInGroup(selectedIds, groupIds);
            const allSelected = groupIds.length > 0 && selectedInGroup === groupIds.length;
            const someSelected = selectedInGroup > 0 && !allSelected;
            return (
              <Accordion
                key={key}
                disableGutters
                elevation={0}
                expanded={expanded.includes(key)}
                onChange={(_, isExpanded) => setGroupExpanded(key, isExpanded)}
                sx={{
                  '&:before': { display: 'none' },
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                >
                  <FormControlLabel
                    onClick={(ev) => ev.stopPropagation()}
                    onFocus={(ev) => ev.stopPropagation()}
                    control={
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(ev) => onChange(toggleSelectedGroup(selectedIds, groupIds, ev.target.checked))}
                        inputProps={{ 'aria-label': `Pilih semua ${group.name}` }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {group.name}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {selectedInGroup}/{groupIds.length}
                        </Typography>
                      </Typography>
                    }
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1.25, px: 2, maxHeight: 280, overflow: 'auto' }}>
                  <FormGroup>
                    {group.questions.map((question) => {
                      const id = String(question.documentId ?? '').trim();
                      if (!id) return null;
                      const title = questionCheckboxLabel(question);
                      const type = String(question.answer_type ?? '').trim();
                      return (
                        <FormControlLabel
                          key={id}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedSet.has(id)}
                              onChange={(ev) => onChange(toggleSelectedId(selectedIds, id, ev.target.checked))}
                              inputProps={{ 'aria-label': title }}
                            />
                          }
                          label={
                            <Typography variant="body2" component="span">
                              <ChemInline text={title} />
                              {type ? (
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {' '}
                                  · {type}
                                </Typography>
                              ) : null}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
      {required && !selectedIds.length ? (
        <FormHelperText error>Pilih minimal satu pertanyaan.</FormHelperText>
      ) : (
        <FormHelperText>Dikelompokkan per nama form. Buka form untuk memilih pertanyaannya.</FormHelperText>
      )}
    </Box>
  );
}
